<script setup>
import { RouterLink } from 'vue-router'
import {
  Check, ArrowRight, Upload, Sparkles, Download,
  FileText, Receipt, ClipboardList, Zap, LineChart, Plus,
  ShieldCheck,
} from 'lucide-vue-next'
import HeroVisual from '@/components/marketing/HeroVisual.vue'
import DemoTryIt from '@/components/marketing/DemoTryIt.vue'
import HouseAd from '@/components/houseads/HouseAd.vue'
import { TIERS, ENTERPRISE, FREE_PAGES } from '@/data/pricing'
import { GUIDES } from '@/data/guides'
import { usePageSeo } from '@/composables/usePageSeo'

usePageSeo({
  // Home canonical is the site root; title omitted so it uses the brand default.
  // Org + WebApplication JSON-LD is emitted sitewide from App.vue — don't repeat it here.
  path: '',
  description: 'Upload an invoice, receipt, purchase order, utility bill or financial report. Get clean structured data — JSON or CSV — in under 15 seconds.',
})

const docTypes = [
  {
    title: 'Invoice',
    icon: FileText,
    body: 'Vendor invoices with line items, taxes, and due dates.',
    fields: ['invoice_no', 'vendor', 'line_items', 'tax', 'total', 'due_date'],
  },
  {
    title: 'Receipt',
    icon: Receipt,
    body: 'Coffee shop, hardware store, ride-share — phone photos welcome.',
    fields: ['merchant', 'date', 'items', 'payment', 'total'],
  },
  {
    title: 'Purchase order',
    icon: ClipboardList,
    body: 'PO numbers, ship-to, terms, line items — pulled cleanly.',
    fields: ['po_number', 'vendor', 'ship_to', 'items', 'terms'],
  },
  {
    title: 'Utility bill',
    icon: Zap,
    body: 'Electricity, gas, water, internet — usage and charges, parsed.',
    fields: ['account', 'period', 'usage', 'charges', 'due_date'],
  },
  {
    title: 'Financial report',
    icon: LineChart,
    body: 'P&L, balance sheets, monthly statements — line items extracted.',
    fields: ['revenue', 'expenses', 'period', 'items'],
  },
  {
    title: 'Custom schema',
    icon: Plus,
    body: "We're adding new types every month. Don't see yours? Define a custom schema or tell us.",
    fields: ['your_field_1', 'your_field_2', '…'],
    custom: true,
  },
]

const tiers = TIERS.map((t) => ({
  name: t.name,
  price: `$${t.monthly}`,
  quota: t.pages.toLocaleString('en-US'),
  features: t.features,
  cta: t.cta,
  primary: t.featured,
  featured: t.featured,
}))

const trustLogos = [
  { name: 'QuickBooks' },
  { name: 'Xero' },
  { name: 'Sheets' },
  { name: 'Zapier' },
  { name: 'Notion' },
]
</script>

<template>
  <div class="home">
    <!-- ─── HERO ─────────────────────────────────────────── -->
    <section class="hero">
      <div class="hero-bg" aria-hidden="true" />
      <div class="container-app hero-inner">
        <div class="hero-grid">
          <div>
            <span class="eyebrow">
              <span class="dot" />
              Document intelligence by Keelara LLC
            </span>
            <h1 class="hero-title">
              Snap a document.<br />
              <span class="accent">Get your data.</span>
            </h1>
            <p class="hero-sub">
              Upload an invoice, receipt, purchase order, utility bill or financial report. Get clean structured data — JSON or CSV — in under 15 seconds. No manual typing. No spreadsheets.
            </p>
            <div class="hero-cta">
              <RouterLink to="/signup" class="btn-primary btn-lg">
                Get started free
                <ArrowRight class="h-4 w-4" :stroke-width="2.2" />
              </RouterLink>
              <a href="#demo" class="btn-secondary btn-lg">See how it works</a>
            </div>
            <div class="hero-trust">
              <Check class="h-3.5 w-3.5" :stroke-width="2.5" />
              <span>
                <strong>Free — {{ FREE_PAGES }} pages, enough to see the quality.</strong> No credit card.
              </span>
            </div>
          </div>

          <HeroVisual />
        </div>
      </div>
    </section>

    <!-- ─── TRUST STRIP ──────────────────────────────────── -->
    <section class="trust-strip">
      <div class="container-app trust-row">
        <p class="trust-text">
          Used by accountants, bookkeepers, and small businesses across <strong>40+ countries</strong> to skip manual data entry.
        </p>
        <div class="trust-logos" aria-hidden="true">
          <span v-for="logo in trustLogos" :key="logo.name" class="trust-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <path d="M3 9h18M9 3v18" />
            </svg>
            {{ logo.name }}
          </span>
        </div>
      </div>
    </section>

    <!-- ─── LIVE DEMO ────────────────────────────────────── -->
    <section id="demo" class="section">
      <div class="container-app">
        <div class="section-head">
          <span class="eyebrow-text">Live demo</span>
          <h2 class="section-title">Try it on your own document.</h2>
          <p class="section-sub">
            Drop a real invoice, receipt, or bill — no account, nothing stored. See exactly what Papersnap extracts in seconds.
          </p>
        </div>

        <div class="mx-auto max-w-xl">
          <DemoTryIt />
        </div>
      </div>
    </section>

    <!-- ─── HOW IT WORKS ─────────────────────────────────── -->
    <section id="how" class="section tight section-banded">
      <div class="container-app">
        <div class="section-head">
          <span class="eyebrow-text">How it works</span>
          <h2 class="section-title">Three steps, fifteen seconds.</h2>
          <p class="section-sub">Drop a file. Get clean data. Pipe it anywhere.</p>
        </div>

        <div class="steps">
          <div class="card-surface step">
            <div class="step-num">01</div>
            <div class="step-icon">
              <Upload class="h-6 w-6" :stroke-width="1.8" />
            </div>
            <h3 class="step-title">Upload</h3>
            <p class="step-body">
              Drag and drop any PDF or image. Photos of paper receipts, scanned bills, vendor PDFs — all work.
            </p>
          </div>
          <div class="card-surface step">
            <div class="step-num">02</div>
            <div class="step-icon">
              <Sparkles class="h-6 w-6" :stroke-width="1.8" />
            </div>
            <h3 class="step-title">Extract</h3>
            <p class="step-body">
              Our AI classifies the doc type and extracts every field with a confidence score. Inspect, edit, accept.
            </p>
          </div>
          <div class="card-surface step">
            <div class="step-num">03</div>
            <div class="step-icon">
              <Download class="h-6 w-6" :stroke-width="1.8" />
            </div>
            <h3 class="step-title">Download</h3>
            <p class="step-body">
              Get clean JSON or CSV — pipe it into your accounting software, spreadsheet, or API. Webhooks too.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── DOCUMENT TYPES ───────────────────────────────── -->
    <section id="types" class="section">
      <div class="container-app">
        <div class="section-head">
          <span class="eyebrow-text">Coverage</span>
          <h2 class="section-title">Built for the documents you actually deal with.</h2>
          <p class="section-sub">Every type below ships with a tuned extraction schema — no prompt engineering required.</p>
        </div>

        <div class="doc-grid">
          <div
            v-for="doc in docTypes"
            :key="doc.title"
            class="card-surface doc-card"
            :class="doc.custom ? 'doc-card-custom' : ''"
          >
            <div class="doc-card-head">
              <span class="doc-card-icon">
                <component :is="doc.icon" class="h-5 w-5" :stroke-width="1.8" />
              </span>
              <div class="doc-card-title">{{ doc.title }}</div>
            </div>
            <p class="doc-card-body">{{ doc.body }}</p>
            <div class="doc-card-fields">
              <span v-for="field in doc.fields" :key="field" class="field">{{ field }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── PRICING ──────────────────────────────────────── -->
    <section id="pricing" class="section tight section-banded">
      <div class="container-app">
        <div class="section-head">
          <span class="eyebrow-text">Pricing</span>
          <h2 class="section-title">Pricing that scales with your stack.</h2>
          <p class="section-sub">Start free. Upgrade when your inbox demands it.</p>
        </div>

        <div class="pricing-grid">
          <div
            v-for="tier in tiers"
            :key="tier.name"
            class="card-surface tier"
            :class="tier.featured ? 'tier-featured' : ''"
          >
            <span v-if="tier.featured" class="tier-badge">Most popular</span>
            <div class="tier-name" :class="tier.featured ? 'tier-name-featured' : ''">{{ tier.name }}</div>
            <div class="tier-price">
              <span class="amount">{{ tier.price }}</span>
              <span class="per">/mo</span>
            </div>
            <div class="tier-quota">
              <strong>{{ tier.quota }}</strong> pages/month
            </div>
            <ul class="tier-features">
              <li v-for="feat in tier.features" :key="feat">
                <Check class="h-4 w-4" :stroke-width="2.5" />
                {{ feat }}
              </li>
            </ul>
            <RouterLink to="/signup" class="tier-cta" :class="tier.primary ? 'btn-primary' : 'btn-secondary'">
              {{ tier.cta }}
            </RouterLink>
          </div>
        </div>

        <div class="card-surface enterprise-strip">
          <div>
            <div class="enterprise-name">{{ ENTERPRISE.name }}</div>
            <p class="enterprise-body">{{ ENTERPRISE.body }}</p>
          </div>
          <a class="btn-secondary" :href="ENTERPRISE.href">{{ ENTERPRISE.cta }}</a>
        </div>

        <p class="pricing-foot">
          Annual billing saves 2 months. Paid plans add pay-as-you-go overage — you set the budget, we confirm every charge.
          <RouterLink to="/pricing">See full feature comparison →</RouterLink>
        </p>
      </div>
    </section>

    <!-- ─── SECURITY ─────────────────────────────────────── -->
    <section id="security" class="section">
      <div class="container-app">
        <div class="security card-surface">
          <div class="security-icon">
            <ShieldCheck class="h-8 w-8" :stroke-width="1.8" />
          </div>
          <div class="security-body-wrap">
            <h3 class="security-title">Your documents stay yours.</h3>
            <p class="security-body">
              Documents and extracted data are stored for 30 days, then automatically deleted. We never sell or share data. SOC 2 Type II audit in progress and per-user data isolation built in.
            </p>
          </div>
          <ul class="security-bullets">
            <li><Check class="h-4 w-4" :stroke-width="2.5" />Auto-deleted after 30 days</li>
            <li><Check class="h-4 w-4" :stroke-width="2.5" />Encrypted at rest and in transit</li>
            <li><Check class="h-4 w-4" :stroke-width="2.5" />Per-user data isolation</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ─── FINAL CTA ────────────────────────────────────── -->
    <section class="final-cta">
      <div class="container-app final-cta-inner">
        <h2>Stop typing. Start snapping.</h2>
        <p>Free for {{ FREE_PAGES }} pages a month — see the extraction quality on your own documents, no credit card required.</p>
        <RouterLink to="/signup" class="btn-primary btn-xl">
          Get started free
          <ArrowRight class="h-4 w-4" :stroke-width="2.4" />
        </RouterLink>
      </div>
    </section>
    <!-- ─── GUIDES (internal links to SEO answer pages) ──── -->
    <section id="guides" class="guides">
      <div class="container-app">
        <h2 class="guides-title">Guides &amp; answers</h2>
        <p class="guides-intro">Practical, search-friendly guides — no signup needed.</p>
        <ul class="guides-grid">
          <li v-for="g in GUIDES" :key="g.path">
            <RouterLink :to="g.path" class="guides-link">{{ g.label }}</RouterLink>
          </li>
        </ul>
      </div>
    </section>

    <section class="px-4 py-12">
      <HouseAd />
    </section>
  </div>
</template>

<style scoped>
/* ============================================================
   Design tokens — direct port of papersnap-home.css from the
   Claude Designer export. Light + dark variable sets so every
   color reference below is theme-aware in one place.
   ============================================================ */
.home {
  --font-display: 'Poppins', system-ui, sans-serif;

  /* Surfaces (light) */
  --psnap-surface-page:   rgb(248 250 252);
  --psnap-surface-card:   #ffffff;
  --psnap-surface-muted:  rgb(241 245 249);
  --psnap-border-soft:    rgb(226 232 240);
  --psnap-border-strong:  rgb(203 213 225);

  --psnap-fg-1: rgb(15 23 42);
  --psnap-fg-2: rgb(71 85 105);
  --psnap-fg-3: rgb(100 116 139);
  --psnap-fg-4: rgb(148 163 184);
}
:global(html.dark .home) {
  --psnap-surface-page:   rgb(2 6 23);
  --psnap-surface-card:   rgb(15 23 42);
  --psnap-surface-muted:  rgb(30 41 59);
  --psnap-border-soft:    rgb(30 41 59);
  --psnap-border-strong:  rgb(51 65 85);

  --psnap-fg-1: #ffffff;
  --psnap-fg-2: rgb(203 213 225);
  --psnap-fg-3: rgb(148 163 184);
  --psnap-fg-4: rgb(100 116 139);
}

/* ── Eyebrow + helpers ──────────────────────────────── */
.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  border-radius: 9999px;
  background: rgb(var(--color-primary) / 0.10);
  border: 1px solid rgb(var(--color-primary) / 0.20);
  color: rgb(var(--color-primary));
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.eyebrow .dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: rgb(var(--color-primary));
  animation: pulse 2.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
}

/* ── Button size variants (extend the global utilities) ── */
.btn-lg {
  padding: 14px 28px;
  font-size: 15px;
  border-radius: 12px;
}
.btn-xl {
  padding: 16px 32px;
  font-size: 16px;
  border-radius: 12px;
}

/* ── Hero ───────────────────────────────────────────── */
.hero {
  position: relative;
  padding: 64px 0 48px;
  overflow: hidden;
}
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.hero-bg::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -160px;
  width: 880px;
  height: 480px;
  transform: translateX(-50%);
  background: radial-gradient(ellipse at center,
    rgb(var(--color-primary) / 0.18) 0%,
    rgb(var(--color-secondary) / 0.10) 40%,
    transparent 70%);
  filter: blur(56px);
}
.hero-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgb(226 232 240 / 0.5) 1px, transparent 1px),
    linear-gradient(90deg, rgb(226 232 240 / 0.5) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 80%);
  -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 80%);
  opacity: 0.7;
}
:global(html.dark .hero-bg::after) {
  background-image:
    linear-gradient(rgb(51 65 85 / 0.4) 1px, transparent 1px),
    linear-gradient(90deg, rgb(51 65 85 / 0.4) 1px, transparent 1px);
}
.hero-inner {
  position: relative;
  z-index: 1;
}
.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 56px;
  align-items: center;
}
@media (max-width: 980px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

.hero-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: clamp(40px, 5.6vw, 68px);
  line-height: 1.02;
  letter-spacing: -0.035em;
  color: var(--psnap-fg-1);
  margin: 20px 0 0;
  text-wrap: balance;
}
.hero-title .accent {
  color: rgb(var(--color-primary));
  background: linear-gradient(180deg,
    rgb(var(--color-primary)) 0%,
    rgb(var(--color-secondary)) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.hero-sub {
  font-size: 17px;
  line-height: 1.55;
  color: var(--psnap-fg-2);
  margin: 22px 0 0;
  max-width: 32rem;
  text-wrap: pretty;
}
.hero-cta {
  display: flex;
  gap: 12px;
  margin-top: 32px;
  flex-wrap: wrap;
}
.hero-trust {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  color: var(--psnap-fg-3);
  font-size: 13px;
}
.hero-trust strong {
  color: var(--psnap-fg-2);
  font-weight: 600;
}
.hero-trust :deep(svg) { color: rgb(var(--color-primary)); }

/* ── Trust strip ────────────────────────────────────── */
.trust-strip {
  border-top: 1px solid var(--psnap-border-soft);
  border-bottom: 1px solid var(--psnap-border-soft);
  padding: 28px 0;
  background: rgb(241 245 249 / 0.5);
}
:global(html.dark .trust-strip) {
  background: rgb(15 23 42 / 0.5);
}
.trust-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: wrap;
}
.trust-text {
  font-size: 13px;
  color: var(--psnap-fg-3);
  max-width: 28rem;
}
.trust-text strong {
  color: var(--psnap-fg-1);
  font-weight: 600;
}
.trust-logos {
  display: flex;
  align-items: center;
  gap: 28px;
  color: var(--psnap-fg-4);
}
.trust-logo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: var(--psnap-fg-3);
  white-space: nowrap;
  opacity: 0.85;
  transition: opacity 0.15s ease, color 0.15s ease;
}
.trust-logo:hover {
  opacity: 1;
  color: var(--psnap-fg-2);
}
.trust-logo svg {
  width: 22px;
  height: 22px;
}

/* ── Section wrappers ───────────────────────────────── */
.section {
  padding: 96px 0;
}
.section.tight {
  padding: 72px 0;
}
.section-banded {
  background: rgb(241 245 249 / 0.4);
  border-top: 1px solid var(--psnap-border-soft);
  border-bottom: 1px solid var(--psnap-border-soft);
}
:global(html.dark .section-banded) {
  background: rgb(15 23 42 / 0.4);
}
.section-head {
  max-width: 44rem;
  margin: 0 auto 56px;
  text-align: center;
}
.eyebrow-text {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgb(var(--color-primary));
  margin-bottom: 12px;
}
.section-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: clamp(28px, 3.6vw, 42px);
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: var(--psnap-fg-1);
  margin: 0;
  text-wrap: balance;
}
.section-sub {
  font-size: 16px;
  color: var(--psnap-fg-2);
  margin: 16px auto 0;
  max-width: 36rem;
  text-wrap: pretty;
}

/* ── Guides (internal links) ────────────────────────── */
.guides {
  padding: 48px 0;
  border-top: 1px solid var(--psnap-border-soft);
}
.guides-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: var(--psnap-fg-1);
  margin: 0;
}
.guides-intro {
  font-size: 14px;
  color: var(--psnap-fg-2);
  margin: 6px 0 0;
}
.guides-grid {
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 24px;
}
@media (min-width: 768px) {
  .guides-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
.guides-link {
  font-size: 14px;
  color: var(--psnap-fg-2);
  text-decoration: none;
}
.guides-link:hover {
  color: rgb(var(--color-primary));
  text-decoration: underline;
}

/* ── Live demo ──────────────────────────────────────── */
.demo-card {
  padding: 0;
  overflow: hidden;
}
.demo-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  min-height: 360px;
}
@media (max-width: 900px) {
  .demo-grid { grid-template-columns: 1fr; }
}
.demo-drop {
  padding: 32px;
  border-right: 1px solid var(--psnap-border-soft);
  display: flex;
  flex-direction: column;
  justify-content: center;
  background:
    radial-gradient(circle at 30% 30%, rgb(var(--color-primary) / 0.06), transparent 60%),
    var(--psnap-surface-card);
}
@media (max-width: 900px) {
  .demo-drop {
    border-right: 0;
    border-bottom: 1px solid var(--psnap-border-soft);
  }
}
.dropzone {
  border: 2px dashed var(--psnap-border-strong);
  border-radius: 14px;
  padding: 40px 24px;
  text-align: center;
  background: rgb(var(--color-primary) / 0.03);
  transition: all 0.2s ease;
  cursor: pointer;
}
.dropzone:hover {
  border-color: rgb(var(--color-primary) / 0.5);
  background: rgb(var(--color-primary) / 0.06);
}
.dropzone.is-active {
  border-color: rgb(var(--color-primary));
  background: rgb(var(--color-primary) / 0.08);
}
.dropzone-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgb(var(--color-primary) / 0.10);
  color: rgb(var(--color-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.dropzone-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 17px;
  color: var(--psnap-fg-1);
  margin-bottom: 6px;
}
.dropzone-sub {
  font-size: 13px;
  color: var(--psnap-fg-3);
  margin-bottom: 14px;
}
.dropzone-types {
  display: inline-flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}
.dropzone-types .chip {
  padding: 3px 9px;
  border-radius: 9999px;
  background: var(--psnap-surface-muted);
  border: 1px solid var(--psnap-border-soft);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  color: var(--psnap-fg-3);
}

.demo-progress {
  margin-top: 22px;
  display: grid;
  gap: 10px;
}
.demo-step {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}
.demo-step .num {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: var(--psnap-surface-muted);
  border: 1px solid var(--psnap-border-soft);
  color: var(--psnap-fg-3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.demo-step.done .num {
  background: rgb(16 185 129);
  border-color: rgb(16 185 129);
  color: white;
}
.demo-step.active .num {
  background: rgb(var(--color-primary));
  border-color: rgb(var(--color-primary));
  color: white;
}
.demo-step .label {
  color: var(--psnap-fg-2);
  flex: 1;
}
.demo-step.done .label,
.demo-step.active .label {
  color: var(--psnap-fg-1);
  font-weight: 500;
}
.demo-step .meta {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  color: var(--psnap-fg-4);
}

.demo-result {
  padding: 28px;
  background: rgb(248 250 252);
  display: flex;
  flex-direction: column;
}
:global(html.dark .demo-result) { background: rgb(2 6 23 / 0.4); }
.demo-result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.demo-result-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px;
  color: var(--psnap-fg-3);
}
.demo-result-fields {
  display: grid;
  gap: 8px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px;
  background: var(--psnap-surface-card);
  border: 1px solid var(--psnap-border-soft);
  border-radius: 10px;
  padding: 14px 16px;
  flex: 1;
}
.demo-field {
  display: grid;
  grid-template-columns: 7em 1fr auto;
  gap: 10px;
  align-items: center;
}
.demo-field .k { color: var(--psnap-fg-3); }
.demo-field .v {
  color: var(--psnap-fg-1);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.demo-field .c {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 9999px;
  background: rgb(16 185 129 / 0.14);
  color: rgb(16 185 129);
  border: 1px solid rgb(16 185 129 / 0.3);
}
.demo-field .c.med {
  background: rgb(245 158 11 / 0.14);
  color: rgb(180 83 9);
  border-color: rgb(245 158 11 / 0.3);
}
:global(html.dark .demo-field .c.med) { color: rgb(252 211 77); }

.demo-cta-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}
.demo-format-toggle {
  display: inline-flex;
  padding: 3px;
  background: var(--psnap-surface-muted);
  border-radius: 9px;
  border: 1px solid var(--psnap-border-soft);
}
.demo-format-toggle button {
  padding: 6px 12px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 700;
  color: var(--psnap-fg-3);
  background: transparent;
  border: 0;
  cursor: pointer;
}
.demo-format-toggle button.active {
  background: var(--psnap-surface-card);
  color: var(--psnap-fg-1);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

/* ── How it works ───────────────────────────────────── */
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
@media (max-width: 900px) {
  .steps { grid-template-columns: 1fr; }
}
.step { padding: 32px 28px; }
.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgb(var(--color-primary) / 0.10);
  color: rgb(var(--color-primary));
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 18px;
}
.step-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgb(var(--color-primary) / 0.10);
  color: rgb(var(--color-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}
.step-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 19px;
  color: var(--psnap-fg-1);
  margin: 0 0 8px;
}
.step-body {
  font-size: 14px;
  color: var(--psnap-fg-2);
  line-height: 1.55;
  margin: 0;
}

/* ── Document types ─────────────────────────────────── */
.doc-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
@media (max-width: 980px) { .doc-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .doc-grid { grid-template-columns: 1fr; } }
.doc-card {
  padding: 24px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.doc-card::after {
  content: '';
  position: absolute;
  right: -40px;
  bottom: -40px;
  width: 140px;
  height: 140px;
  background: radial-gradient(circle, rgb(var(--color-primary) / 0.08), transparent 70%);
  opacity: 0;
  transition: opacity 0.25s ease;
}
.doc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06);
  border-color: rgb(var(--color-primary) / 0.3);
}
.doc-card:hover::after { opacity: 1; }
.doc-card-custom {
  background: linear-gradient(135deg, rgb(var(--color-primary) / 0.04), rgb(var(--color-secondary) / 0.04));
  border-style: dashed;
}
.doc-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.doc-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgb(var(--color-primary) / 0.10);
  color: rgb(var(--color-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.doc-card-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: var(--psnap-fg-1);
}
.doc-card-body {
  font-size: 13px;
  color: var(--psnap-fg-2);
  line-height: 1.5;
  margin: 0 0 14px;
}
.doc-card-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
}
.doc-card-fields .field {
  padding: 3px 8px;
  border-radius: 6px;
  background: var(--psnap-surface-muted);
  color: var(--psnap-fg-3);
  border: 1px solid var(--psnap-border-soft);
}

/* ── Pricing ────────────────────────────────────────── */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}
@media (max-width: 1100px) { .pricing-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px)  { .pricing-grid { grid-template-columns: 1fr; } }
.tier {
  position: relative;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
}
.tier-featured {
  border: 2px solid rgb(var(--color-primary)) !important;
  box-shadow: 0 0 0 1px rgb(var(--color-primary) / 0.12), 0 8px 28px -8px rgb(var(--color-primary) / 0.30);
  transform: translateY(-6px);
}
@media (max-width: 1100px) {
  .tier-featured { transform: none; }
}
.tier-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 12px;
  border-radius: 9999px;
  background: rgb(var(--color-primary));
  color: white;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.08);
}
.tier-name {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.10em;
  color: var(--psnap-fg-3);
  margin-bottom: 8px;
}
.tier-name-featured { color: rgb(var(--color-primary)); }
.tier-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-family: 'Poppins', system-ui, sans-serif;
  margin-bottom: 6px;
}
.tier-price .amount {
  font-size: 38px;
  font-weight: 700;
  color: var(--psnap-fg-1);
  letter-spacing: -0.02em;
}
.tier-price .per {
  font-size: 13px;
  color: var(--psnap-fg-3);
  font-weight: 500;
}
.tier-quota {
  font-size: 13px;
  color: var(--psnap-fg-2);
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--psnap-border-soft);
}
.tier-quota strong {
  color: var(--psnap-fg-1);
  font-weight: 600;
}
.tier-features {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: grid;
  gap: 10px;
  flex: 1;
}
.tier-features li {
  display: flex;
  gap: 8px;
  font-size: 13px;
  color: var(--psnap-fg-2);
}
.tier-features :deep(svg) {
  flex-shrink: 0;
  color: rgb(var(--color-primary));
  margin-top: 2px;
}
.tier-cta {
  width: 100%;
  justify-content: center;
}

.enterprise-strip {
  margin-top: 18px;
  padding: 22px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.enterprise-name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: var(--psnap-fg-1);
}
.enterprise-body {
  font-size: 13px;
  color: var(--psnap-fg-2);
  margin: 4px 0 0;
  text-wrap: pretty;
}

.pricing-foot {
  text-align: center;
  margin-top: 28px;
  font-size: 13px;
  color: var(--psnap-fg-3);
}
.pricing-foot :deep(a) {
  color: rgb(var(--color-primary));
  font-weight: 600;
}
.pricing-foot :deep(a:hover) { text-decoration: underline; }

/* ── Security callout ───────────────────────────────── */
.security {
  padding: 48px 56px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 40px;
  align-items: center;
  background: linear-gradient(135deg, rgb(15 23 42) 0%, rgb(30 41 59) 100%);
  color: rgb(241 245 249);
  border: 0;
  position: relative;
  overflow: hidden;
}
.security::before {
  content: '';
  position: absolute;
  right: -100px;
  top: -80px;
  width: 360px;
  height: 360px;
  background: radial-gradient(circle, rgb(var(--color-primary) / 0.18), transparent 70%);
  pointer-events: none;
}
@media (max-width: 900px) {
  .security {
    padding: 32px;
    grid-template-columns: 1fr;
    gap: 24px;
    text-align: left;
  }
}
.security-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgb(var(--color-primary));
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 6px rgb(var(--color-primary) / 0.18), 0 10px 15px -3px rgb(0 0 0 / 0.08);
  flex-shrink: 0;
}
.security-body-wrap { position: relative; z-index: 1; }
.security-title {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: 26px;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin: 0 0 8px;
  color: white;
}
.security-body {
  font-size: 14px;
  color: rgb(203 213 225);
  line-height: 1.55;
  max-width: 36rem;
  margin: 0;
}
.security-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;
  font-size: 13px;
  position: relative;
  z-index: 1;
}
.security-bullets li {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: rgb(226 232 240);
  white-space: nowrap;
}
.security-bullets :deep(svg) {
  flex-shrink: 0;
  color: rgb(var(--color-primary));
  margin-top: 2px;
}

/* ── Final CTA ──────────────────────────────────────── */
.final-cta {
  text-align: center;
  padding: 96px 0 120px;
  position: relative;
  overflow: hidden;
}
.final-cta::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  width: 800px;
  height: 400px;
  transform: translateX(-50%);
  background: radial-gradient(ellipse, rgb(var(--color-primary) / 0.16), transparent 70%);
  filter: blur(40px);
  pointer-events: none;
}
.final-cta-inner {
  position: relative;
  z-index: 1;
}
.final-cta h2 {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: clamp(36px, 5vw, 56px);
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin: 0 0 18px;
  color: var(--psnap-fg-1);
}
.final-cta p {
  font-size: 17px;
  color: var(--psnap-fg-2);
  margin: 0 auto 32px;
  max-width: 32rem;
}
</style>
